import { minimumPremiumPayment, maximumPremiumPayment, pdfUrlPattern } from '../shared/constants';
import { AvailableInvestmentStrategies } from './AvailableInvestmentStrategies';
import { AvailableDateRanges, IndexDateRangeConfig } from './AvailableDateRanges';
import { Option, some, none } from 'fp-ts/lib/Option';
import { CalculatorURLConfig } from './CalculatorURLConfig';
import { allIndices } from './CalculatorInputs';

interface CalculatorConfigData {
    readonly minimumPremiumPayment: number;
    readonly maximumPremiumPayment: number;
    readonly availableInvestmentStrategies: AvailableInvestmentStrategies;
    readonly availableDateRanges: AvailableDateRanges;

    readonly calculatorUrl: string | null;
    readonly exportUrl: string | null;
    readonly pdfUrlPattern: string;
    readonly shareClass: Option<string>;
    readonly productCusip: Option<string>;
    readonly userIdentifier: Option<string>;


}

export class CalculatorConfig {

    private readonly data: CalculatorConfigData;

    readonly minimumPremiumPayment: number;
    readonly maximumPremiumPayment: number;
    readonly availableInvestmentStrategies: AvailableInvestmentStrategies;
    readonly availableDateRanges: AvailableDateRanges;

    readonly calculatorUrl: string | null;
    readonly exportUrl: string | null;
    readonly pdfUrlPattern: string;
    readonly shareClass: Option<string>;
    readonly productCusip: Option<string>;
    readonly userIdentifier: Option<string>;

    constructor(data: CalculatorConfigData) {
        this.data = data;
        this.minimumPremiumPayment = data.minimumPremiumPayment;
        this.maximumPremiumPayment = data.maximumPremiumPayment;
        this.availableInvestmentStrategies = data.availableInvestmentStrategies;
        this.availableDateRanges = data.availableDateRanges;
        this.calculatorUrl = data.calculatorUrl;
        this.exportUrl = data.exportUrl;
        this.pdfUrlPattern = data.pdfUrlPattern;
        this.shareClass = data.shareClass;
        this.productCusip = data.productCusip;
        this.userIdentifier = data.userIdentifier;
    }

    private copy(data: CalculatorConfigData) {
        return new CalculatorConfig(data);
    }

    withMinimumPremiumPayment(minimumPremiumPayment: number): CalculatorConfig {
        return this.copy({ ... this.data, minimumPremiumPayment });
    }

    withMaximumPremiumPayment(maximumPremiumPayment: number): CalculatorConfig {
        return this.copy({ ... this.data, maximumPremiumPayment });
    }

    withAvailableInvestmentStrategies(availableInvestmentStrategies: AvailableInvestmentStrategies): CalculatorConfig {
        return this.copy({ ... this.data, availableInvestmentStrategies });
    }

    withAvailableDateRanges(availableDateRanges: AvailableDateRanges): CalculatorConfig {
        return this.copy({ ... this.data, availableDateRanges });
    }

    withCalculatorUrl(calculatorUrl: string): CalculatorConfig {
        return this.copy({ ...this.data, calculatorUrl });
    }

    withExportUrl(exportUrl: string): CalculatorConfig {
        return this.copy({ ...this.data, exportUrl });
    }
    withPdfUrlPattern(pdfUrlPattern: string): CalculatorConfig {
        return this.copy({ ...this.data, pdfUrlPattern });
    }

    withShareClass(shareClass: string): CalculatorConfig {
        return this.copy({ ... this.data, shareClass: some(shareClass) });
    }

    withProductCusip(productCusip: string): CalculatorConfig {
        return this.copy({ ...this.data, productCusip: some(productCusip) });
    }

    withUserIdentifier(userId: string): CalculatorConfig {
        return this.copy({ ...this.data, userIdentifier: some(userId) });
    }
}

export interface FetchableConfig {
    strategies: Option<AvailableInvestmentStrategies>;
    dateRanges: Option<AvailableDateRanges>;
    calculatorUrls: Option<CalculatorURLConfig>;
    userIdentifier: Option<string>;
}

const defaultDateRange = { startYear: new Date().getFullYear() - 30, endYear: new Date().getFullYear() - 2 }
const indexDateRanges: Array<IndexDateRangeConfig> = allIndices.map(invIndex => { return { ...defaultDateRange, index: invIndex.code } });
export const emptyCalculatorConfig = new CalculatorConfig({minimumPremiumPayment,
    maximumPremiumPayment,
    availableInvestmentStrategies: new AvailableInvestmentStrategies([]),
    availableDateRanges: new AvailableDateRanges(indexDateRanges),
    calculatorUrl: null,
    exportUrl: null,
    pdfUrlPattern,
    shareClass: none,
    productCusip: none,
    userIdentifier: none});
